/**
 * Core Constants
 * Essential constants for pagination, time, validation, and repositories
 */

// ============================================
// PAGINATION
// ============================================
export const PAGINATION = {
  // General
  DEFAULT_PAGE_SIZE: 20,
  PRODUCT_PAGE_SIZE: 20,
  ORDER_PAGE_SIZE: 20,
  REVIEW_PAGE_SIZE: 10,
  
  // Admin & Partner
  ADMIN_ORDERS_LIMIT: 50,
  PARTNER_ORDERS_LIMIT: 50,
  
  // User
  USER_ORDERS_LIMIT: 10,
  
  // Search
  SEARCH_RESULTS_LIMIT: 20,
  SEARCH_MIN_LENGTH: 2,
  
  // Bulk operations
  BULK_QUERY_LIMIT: 200,
  FILTERED_RESULTS_LIMIT: 100,
  
  // Additional pagination constants
  LINK_ORDERS_LIMIT: 50,
} as const;

// ============================================
// TIME VALUES
// ============================================
export const TIMEVALUES = {
  TOAST_DURATION: 5000,
  STORY_DURATION: 5000,
  STORY_DURATION_MIN: 3000,
  STORY_DURATION_MAX: 15000,
  BANNER_DURATION: 5000,
  ANIMATION_DURATION: 5000,
  RETRY_DELAY_BASE: 1000,
  RETRY_DELAY_MAX: 30000,
  PERMISSION_DENIED_RETRY_DELAY_MS: 1000,
  ONE_DAY_MS: 86400000,
  ONE_HOUR_MS: 3600000,
  ONE_MINUTE_MS: 60000,
  ONE_SECOND_MS: 1000,
  THIRTY_DAYS_MS: 2592000000,
  AUTH_SESSION_TIMEOUT_MS: 2500,
  SOCIAL_TASK_COMPLETION_DELAY_MS: 2000,
  MS_TO_SECONDS_DIVISOR: 1000,
  COPY_RESET_MS: 2000,
  SPLASH_FADE_DELAY_MS: 500,
  SPLASH_HIDE_DELAY_MS: 400,
  LOADING_MINIMUM_MS: 300,
  VITALS_SHOW_DELAY_MS: 2000,
  ANIMATION_TRANSITION_MS: 600,
} as const;

// Legacy alias for backward compatibility
export const TIME = TIMEVALUES;

// ============================================
// NOTIFICATIONS
// ============================================
export const NOTIFICATIONS = {
  POLLING_INTERVAL_MS: 30000,
} as const;

// ============================================
// STOCK & INVENTORY
// ============================================
export const STOCK = {
  LOW_STOCK_THRESHOLD: 5,
  DEFAULT_DROP_STOCK: 10,
  RESERVATION_MINUTES: 30,
  MAX_QTY_PER_ITEM: 10,
} as const;

// ============================================
// SHIPPING
// ============================================
export const SHIPPING = {
  LOCAL_COST: 1500,
  FREE_THRESHOLD: 20000,
  SAME_DAY_CUTOFF_HOUR: 14, // 2 PM
  CAPITAL_CITIES: ['San Miguel de Tucumán'],
} as const;

// ============================================
// LAZY LOADING
// ============================================
export const LAZY_LOAD = {
  INTERSECTION_THRESHOLD: 0.1,
  ROOT_MARGIN: '100px',
} as const;

// ============================================
// IMAGE OPTIMIZATION
// ============================================
export const IMAGE_OPTIMIZATION = {
  WIDTH: 800,
  QUALITY: 80,
  AUTO_FORMAT: true,
  getParams: () => `w=${IMAGE_OPTIMIZATION.WIDTH}&q=${IMAGE_OPTIMIZATION.QUALITY}&auto=format`,
} as const;

// ============================================
// UI ANIMATIONS
// ============================================
export const UI_ANIMATIONS = {
  SEARCH_DEBOUNCE_MS: 300,
  VIBRATE_DURATION_MS: 50,
  FILTER_SCROLL_TIMEOUT_MS: 100,
  SCROLL_THRESHOLD_PX: 5,
  SCROLL_AMOUNT_PX: 200,
  ADMIN_SEARCH_DEBOUNCE_MS: 400,
  ADD_TO_CART_RESET_MS: 3000,
  COUNTDOWN_UPDATE_INTERVAL_MS: 1000,
  STORY_PROGRESS_UPDATE_INTERVAL_MS: 50,
  STORY_LIFETIME_HOURS: 24,
  STORY_DEFAULT_DURATION_MS: 5000,
  STORY_DURATION_SHORT: 3000,
  STORY_DURATION_LONG: 10000,
  GESTURE_SWIPE_THRESHOLD: 10,
} as const;

// ============================================
// UI DIMENSIONS (PX)
// ============================================
export const UI_DIMENSIONS = {
  TOUCH_TARGET_MIN_SIZE: 44,
  PRODUCT_CARD_MIN_WIDTH: 160,
  PRODUCT_CARD_HORIZONTAL_WIDTH: 140,
  STORY_AVATAR_SIZE: 68,
  SHOP_BANNER_HEIGHT: 192, // h-48
  HEADER_HEIGHT: 74,
  NEXT_CATEGORY_BANNER_HEIGHT: 144, // h-36
  EMPTY_CART_HEIGHT: '60vh',
  TOP_UP_BUTTON_HEIGHT: 56, // h-14
  TRACKING_MAP_HEIGHT: 192,
  ADMIN_SIDEBAR_WIDTH: 224, // w-56
  CHART_HEIGHT: 220,
  CHART_WIDTH: 600,
} as const;

// ============================================
// PRICING & MONETARY
// ============================================
export const PRICING = {
  MAX_PRICE_FILTER: 100000,
  TRANSFER_DISCOUNT_THRESHOLD: 40000,
  INSTALLMENT_THRESHOLD: 20000,
  IMPULSE_THRESHOLD: 20000,
} as const;

// ============================================
// FINANCE
// ============================================
export const FINANCE = {
  INSTALLMENT_COUNT: 3,
  TOP_UP_MINIMUM_AMOUNT: 500,
  TOP_UP_DEFAULT_AMOUNTS: [1000, 2000, 5000, 10000],
  BID_INCREMENT_AMOUNTS: [1000, 2000, 5000],
  SOCIAL_TASK_POINTS_DEFAULT: 500,
  SOCIAL_TASK_POINTS_SECONDARY: 300,
  REWARD_SHIPPING_COST: 1000,
  DEFAULT_PROFIT_MARGIN_PERCENT: 30,
  VIEWERS_ESTIMATE_DIVISOR: 10,
} as const;

// ============================================
// DEFAULT VALUES
// ============================================
export const DEFAULTS = {
  ZIP_CODE: '4000',
  PRODUCT_RATING: 4.5,
  CARD_EXP_MONTH: 12,
  CARD_EXP_YEAR: 28,
  PRODUCT_QUANTITY: 1,
} as const;

// ============================================
// IMAGE ASPECT RATIOS
// ============================================
export const IMAGE_RATIOS = {
  CREDIT_CARD: '1.58/1',
  ADDRESS: '2/1',
  SHOP_BANNER: '16/9',
  VIDEO: '16/9',
} as const;

// ============================================
// ANALYTICS
// ============================================
export const ANALYTICS = {
  TOP_LIMIT: 20,
  TOP_LIMIT_SMALL: 10,
  TOP_QUERIES_LIMIT: 20,
  NO_RESULTS_LIMIT: 10,
  VIEWERS_ESTIMATE_DIVISOR: 10,
} as const;

// ============================================
// CHART
// ============================================
export const CHART = {
  HEIGHT: 200,
  WIDTH: 600,
  PADDING_LEFT: 40,
  PADDING_VERTICAL: 20,
  MIN_VALUE: 10,
  AXIS_PERCENTAGES: [0.75, 0.5, 0.25, 0],
  DAYS_BACK: 6,
  LINE_WIDTH: 2,
  POINT_RADIUS: 4,
  CONTAINER_HEIGHT: 250,
} as const;

// ============================================
// DATE FORMATS
// ============================================
export const DATE_FORMATS = {
  DISPLAY: 'DD/MM/YYYY',
  DISPLAY_WITH_TIME: 'DD/MM/YYYY HH:mm',
  ISO: 'YYYY-MM-DD',
  TIME: 'HH:mm',
  RELATIVE: 'relative',
} as const;

// ============================================
// TIMEZONE
// ============================================
export const TIMEZONE = 'America/Argentina/Tucuman';

// ============================================
// REPOSITORIES
// ============================================
export const REPOSITORIES = {
  // Polling intervals
  POLLING_INTERVAL_MS: 30000,
  
  // Batch sizes
  FIRESTORE_BATCH_SIZE: 500,
  FIRESTORE_IN_QUERY_LIMIT: 10,
  FETCH_BATCH_SIZE: 10,
  
  // Pagination
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 200,
  USER_PAGE_SIZE: 20,
  PARTNER_PAGE_SIZE: 20,
  FINANCE_PAGE_SIZE: 20,
  TRANSACTION_LIMIT: 200,
  NOTIFICATION_LIMIT: 20,
  NOTIFICATIONS_KEEP: 50,
  
  // Search
  USER_SEARCH_LIMIT: 100,
} as const;

// ============================================
// CORE LIMITS (Internal use - prefer UI.LIMITS for display limits)
// ============================================
export const CORE_LIMITS = {
  RECENT_TRANSACTIONS: 10,
  NOTIFICATIONS_DISPLAY: 20,
  SHOP_PRODUCTS_PREVIEW: 4,
  RELATED_PRODUCTS: 6,
  STORY_SEARCH_RESULTS: 10,
  CART_SUGGESTIONS_POOL_SIZE: 100,
  RELATED_PRODUCTS_FETCH_MULTIPLIER: 2,
  STORY_PRODUCT_SEARCH_LIMIT: 100,
} as const;

// ============================================
// VALIDATION
// ============================================
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 6,
  NAME_MIN_LENGTH: 3,
  NAME_MAX_LENGTH: 100,
  PHONE_MIN_DIGITS: 10,
  SEARCH_MIN_LENGTH: 2,
  DNI_MIN_LENGTH: 7,
  DNI_MAX_LENGTH: 8,
  DESCRIPTION_MAX_LENGTH: 5000,
} as const;

// ============================================
// CHAT (for backward compatibility)
// ============================================
export const CHAT = {
  MAX_MESSAGE_LENGTH: 1000,
  TYPING_TIMEOUT_MS: 3000,
  MESSAGE_HISTORY_LIMIT: 50,
  POLLING_INTERVAL_MS: 30000,
  BOT_RESPONSE_DELAY_MS: 1000,
  BOT_TYPING_DELAY_MS: 1500,
} as const;
